/**
 * v1.14.60.9.2.0 F3 — Pannello Inviti Cliente (admin Quoter).
 *
 * Lista inviti generati dal broker + crea / revoca / estendi.
 * Endpoint:
 *  - GET    /api/admin/quoter/invites
 *  - POST   /api/admin/quoter/invites
 *  - GET    /api/admin/quoter/invites/{id}
 *  - POST   /api/admin/quoter/invites/{id}/revoke
 *  - POST   /api/admin/quoter/invites/{id}/extend
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Copy, Plus, RefreshCw, Ban, Clock, Eye, FileText, FileSpreadsheet, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axc = { withCredentials: true };

const STATUS_BADGES = {
  draft: { bg: "bg-zinc-200", fg: "text-zinc-700", label: "Bozza" },
  anagrafica_uploaded: { bg: "bg-blue-100", fg: "text-blue-800", label: "Anagrafica ricevuta" },
  garanzie_in_progress: { bg: "bg-amber-100", fg: "text-amber-900", label: "Garanzie in corso" },
  pending_broker_review: { bg: "bg-yellow-100", fg: "text-yellow-900", label: "📋 In attesa di trasmissione" },
  transmitted: { bg: "bg-emerald-100", fg: "text-emerald-800", label: "✓ Trasmessa al cliente" },
  completed: { bg: "bg-emerald-100", fg: "text-emerald-800", label: "Completato (legacy)" },
  expired: { bg: "bg-red-100", fg: "text-red-900", label: "Scaduto" },
  revoked: { bg: "bg-red-200", fg: "text-red-900", label: "Revocato" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_BADGES[status] || STATUS_BADGES.draft;
  return (
    <span
      data-testid={`invite-status-${status}`}
      className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${s.bg} ${s.fg}`}
    >
      {s.label}
    </span>
  );
};

export default function QuoterInvitesPanel({ products = [] }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    product_slug: "composita_reale",
    client_name: "",
    client_email: "",
    client_phone: "",
    notes: "",
    expires_in_days: 30,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : "";
      const { data } = await axios.get(`${API}/admin/quoter/invites${q}`, axc);
      setInvites(data.items || []);
    } catch (e) {
      toast.error(`Errore inviti: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const createInvite = async () => {
    if (!form.product_slug) {
      toast.error("Seleziona un prodotto");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/admin/quoter/invites`, form, axc);
      toast.success(`Invito creato · token ${data.token.slice(0, 10)}…`);
      setShowCreate(false);
      setForm({ ...form, client_name: "", client_email: "", client_phone: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const copyLink = (publicUrl) => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Link copiato negli appunti");
  };

  const revokeInvite = async (id) => {
    if (!confirm("Revocare definitivamente questo invito?")) return;
    try {
      await axios.post(`${API}/admin/quoter/invites/${id}/revoke`, {}, axc);
      toast.success("Invito revocato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const transmitInvite = async (id) => {
    if (!confirm(
      "Confermi di voler trasmettere questa quotazione al cliente?\n\n"
      + "Il cliente potrà scaricare i 3 documenti (PDF, Excel, CGA) "
      + "dalla pagina pubblica.")) return;
    try {
      await axios.post(`${API}/admin/quoter/invites/${id}/transmit`,
        {}, axc);
      toast.success("Quotazione trasmessa al cliente ✓");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail || e.message);
    }
  };

  const extendInvite = async (id) => {
    const days = parseInt(prompt("Giorni aggiuntivi:", "30"), 10);
    if (!days || isNaN(days)) return;
    try {
      await axios.post(`${API}/admin/quoter/invites/${id}/extend`, { additional_days: days }, axc);
      toast.success(`Scadenza estesa di ${days} giorni`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const downloadArtifact = async (inviteId, kind) => {
    try {
      const resp = await axios.get(
        `${API}/admin/quoter/invites/${inviteId}/artifacts/${kind}`,
        { ...axc, responseType: "blob" },
      );
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const ext = kind === "excel" ? "xlsx" : "pdf";
      a.download = `colucci_${kind}_${inviteId.slice(0, 8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} scaricato`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || `Errore download ${kind}`,
      );
    }
  };

  const pendingCount = invites.filter(
    (i) => i.status === "completed"
  ).length;

  return (
    <div className="space-y-3" data-testid="quoter-invites-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            Inviti Cliente
            {pendingCount > 0 && (
              <span
                data-testid="invites-pending-badge"
                className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full"
              >
                {pendingCount} completati
              </span>
            )}
          </h2>
          <p className="text-xs text-zinc-500">
            Genera link condivisibili per la quotazione cliente (anagrafica + wizard garanzie).
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border rounded text-xs px-2 py-1"
            data-testid="invites-status-filter"
          >
            <option value="">Tutti</option>
            <option value="draft">Bozza</option>
            <option value="anagrafica_uploaded">Anagrafica ricevuta</option>
            <option value="garanzie_in_progress">Garanzie in corso</option>
            <option value="completed">Completato</option>
            <option value="expired">Scaduto</option>
            <option value="revoked">Revocato</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="invites-reload-btn">
            <RefreshCw className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            onClick={() => setShowCreate(true)}
            data-testid="invites-create-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Nuovo invito
          </Button>
        </div>
      </div>

      {showCreate && (
        <Card data-testid="invites-create-modal">
          <CardHeader>
            <CardTitle className="text-sm">Crea nuovo invito</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-600">Prodotto *</label>
                <select
                  value={form.product_slug}
                  onChange={(e) => setForm({ ...form, product_slug: e.target.value })}
                  className="w-full border rounded p-1.5 text-sm"
                  data-testid="invite-product-slug"
                >
                  {products.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.nome || p.slug}
                    </option>
                  ))}
                  {!products.length && (
                    <option value="composita_reale">Composita Reale</option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-600">Scadenza (giorni)</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={form.expires_in_days}
                  onChange={(e) => setForm({ ...form, expires_in_days: parseInt(e.target.value, 10) || 30 })}
                  className="w-full border rounded p-1.5 text-sm"
                  data-testid="invite-expires-days"
                />
              </div>
              <input
                placeholder="Nome cliente"
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                className="border rounded p-1.5 text-sm"
                data-testid="invite-client-name"
              />
              <input
                placeholder="Email cliente"
                value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                className="border rounded p-1.5 text-sm"
                data-testid="invite-client-email"
              />
              <input
                placeholder="Telefono"
                value={form.client_phone}
                onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                className="border rounded p-1.5 text-sm"
                data-testid="invite-client-phone"
              />
              <input
                placeholder="Note interne agenzia"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="border rounded p-1.5 text-sm"
                data-testid="invite-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setShowCreate(false)} data-testid="invite-cancel-btn">
                Annulla
              </Button>
              <Button size="sm" onClick={createInvite} data-testid="invite-submit-btn">
                Crea
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-10 text-sm text-zinc-500">Caricamento inviti…</div>
      ) : invites.length === 0 ? (
        <div className="text-center py-10 text-sm text-zinc-500 italic" data-testid="invites-empty">
          Nessun invito creato. Clicca &quot;Nuovo invito&quot; per generare un link al cliente.
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-xs" data-testid="invites-table">
              <thead className="bg-zinc-50 border-b">
                <tr>
                  <th className="text-left p-2">Cliente</th>
                  <th className="text-left p-2">Prodotto</th>
                  <th className="text-left p-2">Stato</th>
                  <th className="text-center p-2">Visite</th>
                  <th className="text-left p-2">Scadenza</th>
                  <th className="text-right p-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} className="border-b hover:bg-zinc-50" data-testid={`invite-row-${inv.id}`}>
                    <td className="p-2">
                      <div className="font-medium">{inv.client_name || "—"}</div>
                      {inv.client_email && <div className="text-[10px] text-zinc-500">{inv.client_email}</div>}
                    </td>
                    <td className="p-2 font-mono text-[10px]">{inv.product_slug}</td>
                    <td className="p-2"><StatusBadge status={inv.status} /></td>
                    <td className="p-2 text-center font-mono">
                      <Eye className="w-3 h-3 inline mr-1" />
                      {inv.view_count || 0}
                    </td>
                    <td className="p-2 text-[10px]">
                      {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td className="p-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyLink(inv.public_url)}
                          title="Copia link"
                          data-testid={`invite-copy-${inv.id}`}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        {inv.status !== "revoked" && inv.status !== "completed" && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => extendInvite(inv.id)}
                              title="Estendi scadenza"
                              data-testid={`invite-extend-${inv.id}`}
                            >
                              <Clock className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => revokeInvite(inv.id)}
                              title="Revoca link"
                              data-testid={`invite-revoke-${inv.id}`}
                            >
                              <Ban className="w-3 h-3 text-red-600" />
                            </Button>
                          </>
                        )}
                        {(inv.status === "completed"
                          || inv.status === "pending_broker_review"
                          || inv.status === "transmitted") && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadArtifact(inv.id, "pdf")}
                              title="Scarica PDF preventivo"
                              data-testid={`invite-dl-pdf-${inv.id}`}
                            >
                              <FileText className="w-3 h-3 text-emerald-700" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadArtifact(inv.id, "excel")}
                              title="Scarica Excel riepilogo"
                              data-testid={`invite-dl-excel-${inv.id}`}
                            >
                              <FileSpreadsheet className="w-3 h-3 text-emerald-700" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadArtifact(inv.id, "cga")}
                              title="Scarica Fascicolo Informativo (CGA)"
                              data-testid={`invite-dl-cga-${inv.id}`}
                            >
                              <BookOpen className="w-3 h-3 text-emerald-700" />
                            </Button>
                            {inv.status === "pending_broker_review" && (
                              <Button
                                size="sm"
                                onClick={() => transmitInvite(inv.id)}
                                title="Trasmetti la quotazione al cliente"
                                data-testid={`invite-transmit-${inv.id}`}
                                className="bg-amber-500 hover:bg-amber-600 text-white ml-1"
                              >
                                📤 Trasmetti
                              </Button>
                            )}
                            {inv.status === "transmitted" && (
                              <span
                                data-testid={`invite-transmitted-badge-${inv.id}`}
                                className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 ml-1"
                              >
                                Trasmessa ✓
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
